import { IconName } from "../components/Icon/types";
import { ISpCodeDisplay, ISubscriptionLabel } from "../types/common";

export const RECURLY = "RECURLY";
export const GOOGLE_PLAY_STORE = "GOOGLE_PLAY_STORE";
export const APPLE_APP_STORE = "APPLE_APP_STORE";
// Amazon Appstore (Fire OS) in-app purchases. Store-managed exactly like
// Apple/Google: no card on file, no in-portal cancel or plan change — the
// customer manages the subscription through Amazon.
export const AMAZON_APP_STORE = "AMAZON_APP_STORE";
export const BDB_GF = "BDB_GF";
export const RESELLER = "RESELLER";
// Crypto subscriptions (paid via BTCPay Server) are routed server-side through
// PhSubscriptionContext under this sp_code — see backend SpCode.PhSubscription.
export const PH_SUBSCRIPTION = "PH_SUBSCRIPTION";

// How a subscription provider is rendered: which icon to draw and which
// translation key holds its name. Keyed by sp_code, and the icon is kept
// separate from the label on purpose - AMAZON_APP_STORE is the Amazon Appstore
// (Fire OS IAP), which must NOT reuse the "amazon" icon, that being Amazon Pay,
// an unrelated Recurly payment method that only shares the brand name.
export const SP_CODE_DISPLAY: Record<string, ISpCodeDisplay> = {
  [RECURLY]: { icon: "recurly", labelKey: RECURLY },
  [APPLE_APP_STORE]: { icon: "apple", labelKey: APPLE_APP_STORE },
  [GOOGLE_PLAY_STORE]: { icon: "google", labelKey: GOOGLE_PLAY_STORE },
  [AMAZON_APP_STORE]: { icon: "amazon-appstore", labelKey: AMAZON_APP_STORE },
  [RESELLER]: { icon: "reseller", labelKey: RESELLER },
  [PH_SUBSCRIPTION]: { icon: "ph-subscription", labelKey: PH_SUBSCRIPTION },
  [BDB_GF]: { icon: "bdb", labelKey: BDB_GF },
};

// The provider icon for an sp_code, or undefined for one we have no icon for
// (UNKNOWN is a parse-failure sentinel, never a provider a subscription is
// actually sold through, so it deliberately has no entry above).
export const getSpCodeIcon = (
  spCode: string | undefined,
): IconName | undefined => {
  return spCode ? SP_CODE_DISPLAY[spCode]?.icon : undefined;
};

export const EXTERNAL_SP_LIST = [
  GOOGLE_PLAY_STORE,
  APPLE_APP_STORE,
  AMAZON_APP_STORE,
];

export const RESELLER_MANAGED_SP_LIST = [RESELLER];

// Subscriptions whose lifecycle (plan change, cancellation) is managed in-portal
// by the PH subscription microservice, rather than by Recurly or an app store.
// The payment method behind it is external (crypto/BTCPay today, but could be
// anything the service supports later) — so, unlike EXTERNAL_SP_LIST, it must
// NOT be added there (that would block the in-portal plan-change/cancel actions).
export const PH_SUBSCRIPTION_MANAGED_SP_LIST = [PH_SUBSCRIPTION];

// Providers whose renewal the portal itself bills and can quote a next bill
// for. Allow-listed on purpose, not "has next_invoice_amount" - app stores and
// resellers carry that field too but bill/renew on their own side.
export const PORTAL_BILLED_SP_LIST = [RECURLY, PH_SUBSCRIPTION];

// Whether the portal drives this subscription's billing cycle.
export const isPortalBilled = (spCode: string | undefined): boolean => {
  return !!spCode && PORTAL_BILLED_SP_LIST.includes(spCode);
};

export const isResellerManaged = (spCode: string | undefined): boolean => {
  return !!spCode && RESELLER_MANAGED_SP_LIST.includes(spCode);
};

// True when the subscription's lifecycle is managed by the PH subscription
// service (crypto/BTCPay today). Use this for in-portal plan-change / cancel
// concerns — NOT for card UI (an app-store sub also has no card; see below).
export const isPhSubscriptionManaged = (
  spCode: string | undefined,
): boolean => {
  return !!spCode && PH_SUBSCRIPTION_MANAGED_SP_LIST.includes(spCode);
};

// True when the subscription has no Recurly card on file — either an app-store
// provider (Apple/Google) or one managed by the PH subscription service
// (crypto/BTCPay). Use this for card-UI / Recurly-only concerns (hiding the card,
// skipping Recurly-only fetches); use isPhSubscriptionManaged for lifecycle.
export const isNonRecurlyPayment = (spCode: string | undefined): boolean => {
  return (
    !!spCode &&
    (EXTERNAL_SP_LIST.includes(spCode) || isPhSubscriptionManaged(spCode))
  );
};

// Whether an sp_code, on its own, names the payment method behind it.
//
// An app store's does: "you pay through the App Store" IS the instrument, the
// customer has no other, and no rail is ever relayed for one — so the sp_code
// label is the whole truth there and must keep being shown.
//
// PH_SUBSCRIPTION's does not. It is the provider that bills EVERY subscription
// the subscription service sells, whatever rail paid for it, so its label
// ("Subscription") names the biller and says nothing about the instrument.
// Only `sp.payment_provider` can name that — and where no subscription is left
// to read it from (an expired customer, whose subscriptions the portal no
// longer lists) the honest answer is that we cannot tell, not "Subscription".
export const spCodeNamesPaymentMethod = (
  spCode: string | undefined,
): boolean => {
  return !!spCode && !isPhSubscriptionManaged(spCode);
};

export const SUBSCRIPTION_LABEL: Record<string, ISubscriptionLabel> = {
  VYPRVPN: {
    logo: "vyprvpn",
    label: "vyprvpn",
  },
  VYPRVPN_CORPORATE: {
    logo: "vyprvpn",
    label: "vyprvpn",
  },
  GIGANEWS: {
    logo: "giganews",
    label: "giganews",
  },
  TEXASNET: {
    logo: "texasnet",
    label: "texasnet",
  },
};
